import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import AppPageTitle from "../../components/AppPageTitle";
import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useTickets from "../../hooks/useTickets";

const DEFAULT_PERIOD_DAYS = 30;

const PERIOD_PRESETS = [
	{ id: "7d", label: "7 dias", days: 7 },
	{ id: "30d", label: "30 dias", days: 30 },
	{ id: "90d", label: "90 dias", days: 90 },
];

const defaultKpi = {
	contactsCount: 0,
	contactsToday: 0,
	ticketsClosedCount: 0,
	messagesSentCount: 0,
	connectionsCount: 0,
	messageCount: 0,
	averageTimeTicketPending: "00h 00m",
	averageTimeTicketOpen: "00h 00m",
	averageTimePendingMinutes: 0,
	averageTimeOpenMinutes: 0,
	averageRating: 0,
	ratingCount: 0,
	leadsByConnection: [],
	dataChartAttendants: [],
	dataProgressDepartmentBar: [],
	dataProgressServiceBar: [],
	dataBarRating: [],
	cloudMessages: {
		messagesFromMe: 0,
		messagesFromClient: 0,
		tickets: 0,
		sessions: 0,
	},
};

function IconCalendar({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<rect x="3" y="4" width="18" height="18" rx="2" />
			<path d="M16 2v4" />
			<path d="M8 2v4" />
			<path d="M3 10h18" />
		</svg>
	);
}

function IconUsers({ className = "size-5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
}

function IconCheck({ className = "size-5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M20 6 9 17l-5-5" />
		</svg>
	);
}

function IconMessage({ className = "size-5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
		</svg>
	);
}

function IconClock({ className = "size-5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<path d="M12 6v6l4 2" />
		</svg>
	);
}

function IconWifi({ className = "size-5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M5 12.55a11 11 0 0 1 14.08 0" />
			<path d="M1.42 9a16 16 0 0 1 21.16 0" />
			<path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
			<path d="M12 20h.01" />
		</svg>
	);
}

function IconSpark({ className = "size-5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
			<path d="M5 3v4" />
			<path d="M3 5h4" />
			<path d="M19 17v4" />
			<path d="M17 19h4" />
		</svg>
	);
}

function IconRefresh({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M23 4v6h-6" />
			<path d="M1 20v-6h6" />
			<path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" />
			<path d="M20.49 15A9 9 0 0 1 6.36 18.36L1 14" />
		</svg>
	);
}

function IconArrowUpRight({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M7 17 17 7" />
			<path d="M7 7h10v10" />
		</svg>
	);
}

function IconStar({ filled = false, className = "size-4", toneClassName = "" }) {
	return (
		<svg
			className={`${className} ${toneClassName}`.trim()}
			viewBox="0 0 24 24"
			fill={filled ? "currentColor" : "none"}
			stroke="currentColor"
			strokeWidth="2"
		>
			<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
		</svg>
	);
}

function getDefaultPeriod(days = DEFAULT_PERIOD_DAYS) {
	const end = new Date();
	const start = new Date();
	start.setDate(start.getDate() - days);

	return {
		start: start.toISOString().slice(0, 10),
		end: end.toISOString().slice(0, 10),
	};
}

function formatAverageTime(apiValue) {
	const rows = !apiValue ? [] : Array.isArray(apiValue[0]) ? apiValue[0] : apiValue;

	if (!rows.length) {
		return { formatted: "00h 00m", minutes: 0 };
	}

	const total = rows.reduce((acc, item) => acc + Number(item.averageTime || 0), 0);
	const avgHours = total / rows.length;
	const hours = Math.floor(avgHours);
	const minutes = Math.round((avgHours - hours) * 60);

	return {
		formatted: `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`,
		minutes: Math.round(avgHours * 60),
	};
}

function formatNumber(value) {
	return Number(value || 0).toLocaleString("pt-BR");
}

function formatDateLabel(value) {
	if (!value) return "-";
	return new Date(`${value}T00:00:00`).toLocaleDateString("pt-BR");
}

function formatDateTime(value) {
	if (!value) return "-";
	return new Date(value).toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function formatChannelLabel(channel) {
	const normalized = String(channel || "").toLowerCase();

	switch (normalized) {
		case "whatsappbusiness":
			return "WhatsApp Business";
		case "instagram":
			return "Instagram";
		case "facebook":
			return "Facebook";
		case "telegram":
			return "Telegram";
		case "email":
			return "E-mail";
		default:
			return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Canal";
	}
}

function getChannelColor(channel) {
	const normalized = String(channel || "").toLowerCase();

	switch (normalized) {
		case "instagram":
			return "#ec4899";
		case "facebook":
			return "#3b82f6";
		case "telegram":
			return "#06b6d4";
		case "email":
			return "#f59e0b";
		case "whatsappbusiness":
			return "#10b981";
		default:
			return "#6366f1";
	}
}

function getAverageRatingLabel(value) {
	if (value >= 4.5) return "Qualidade excelente";
	if (value >= 4) return "Qualidade consistente";
	if (value >= 3) return "Há espaço para melhorar";
	if (value > 0) return "Atenção na experiência";
	return "Sem avaliações recentes";
}

function truncateLabel(value, max = 20) {
	if (!value || value.length <= max) return value;
	return `${value.slice(0, max - 1)}...`;
}

function MetricCard({ title, value, hint, icon, toneClassName, footer }) {
	return (
		<div className="card h-full">
			<div className="card-body flex items-start justify-between gap-4">
				<div className="min-w-0">
					<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
						{title}
					</p>
					<p className="m-0 text-2xl font-semibold text-default-800 dark:text-default-100">
						{value}
					</p>
					{hint ? (
						<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
							{hint}
						</p>
					) : null}
					{footer ? <div className="mt-3">{footer}</div> : null}
				</div>
				<div className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${toneClassName}`}>
					{icon}
				</div>
			</div>
		</div>
	);
}

function SectionCard({ title, subtitle, action, children, bodyClassName = "" }) {
	return (
		<div className="card h-full">
			<div className={`card-body ${bodyClassName}`.trim()}>
				<div className="mb-5 flex flex-wrap items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="mb-1 text-sm font-semibold text-default-800 dark:text-default-100">
							{title}
						</p>
						{subtitle ? (
							<p className="m-0 text-sm text-default-500 dark:text-default-400">
								{subtitle}
							</p>
						) : null}
					</div>
					{action}
				</div>
				{children}
			</div>
		</div>
	);
}

function MiniStat({ label, value, hint, valueClassName = "text-default-900 dark:text-default-50" }) {
	return (
		<div className="rounded-2xl border border-default-200/80 bg-default-50/70 p-4 dark:border-white/10 dark:bg-white/5">
			<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
				{label}
			</p>
			<p className={`m-0 text-xl font-semibold ${valueClassName}`.trim()}>{value}</p>
			{hint ? (
				<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">{hint}</p>
			) : null}
		</div>
	);
}

function InsightCard({ title, description, toneClassName }) {
	return (
		<div className={`rounded-2xl border px-4 py-3 ${toneClassName}`.trim()}>
			<p className="m-0 text-sm font-semibold">{title}</p>
			<p className="mt-1 mb-0 text-sm opacity-90">{description}</p>
		</div>
	);
}

function RankingList({
	items,
	emptyText,
	valueFormatter,
	getBarClassName,
	getBarStyle,
}) {
	const maxValue = items.length ? Math.max(...items.map((item) => Number(item.value) || 0), 1) : 1;

	if (!items.length) {
		return <p className="m-0 text-sm text-default-500 dark:text-default-400">{emptyText}</p>;
	}

	return (
		<div className="space-y-4">
			{items.map((item) => {
				const width = Math.max(((Number(item.value) || 0) / maxValue) * 100, item.value > 0 ? 10 : 0);

				return (
					<div key={item.name} className="space-y-2">
						<div className="flex items-center justify-between gap-3">
							<p className="m-0 truncate text-sm font-medium text-default-700 dark:text-default-200">
								{item.name}
							</p>
							<p className="m-0 shrink-0 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
								{valueFormatter(item)}
							</p>
						</div>
						<div className="h-2.5 overflow-hidden rounded-full bg-default-100 dark:bg-default-800">
							<div
								className={`h-full rounded-full ${getBarClassName ? getBarClassName(item) : "bg-primary"}`.trim()}
								style={
									getBarStyle ? getBarStyle(item, width) : { width: `${width}%` }
								}
							/>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RatingStars({ value, className = "size-4" }) {
	return (
		<div className="flex items-center gap-1">
			{[1, 2, 3, 4, 5].map((star) => (
				<IconStar
					key={star}
					filled={Number(value) >= star}
					className={className}
					toneClassName={Number(value) >= star ? "text-warning" : "text-default-300 dark:text-default-600"}
				/>
			))}
		</div>
	);
}

function DashboardChartTooltip({ active, payload, label, darkMode }) {
	if (!active || !payload?.length) return null;

	const item = payload[0]?.payload;

	return (
		<div
			style={{
				background: darkMode ? "#18181b" : "#ffffff",
				border: `1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
				borderRadius: 12,
				padding: "10px 12px",
				boxShadow: darkMode ? "0 10px 24px rgba(0,0,0,0.35)" : "0 10px 24px rgba(15,23,42,0.08)",
			}}
		>
			<p
				style={{
					margin: 0,
					fontSize: 13,
					fontWeight: 600,
					color: darkMode ? "#f4f4f5" : "#18181b",
				}}
			>
				{item?.fullName || label}
			</p>
			<p
				style={{
					margin: "4px 0 0",
					fontSize: 12,
					color: darkMode ? "#a1a1aa" : "#52525b",
				}}
			>
				{formatNumber(item?.leads || 0)} lead(s) • {item?.channelLabel || "Canal"}
			</p>
		</div>
	);
}

export default function NewDashboard() {
	const { user } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const [period, setPeriod] = useState(getDefaultPeriod);
	const [dashboardData, setDashboardData] = useState(null);
	const [lastRatings, setLastRatings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

	const userQueueIds = user?.queues?.length ? user.queues.map((queue) => queue.id) : [];

	const {
		count: countOpen,
		loading: loadingOpen,
	} = useTickets({
		status: "open",
		showAll: "true",
		withUnreadMessages: "false",
		queueIds: JSON.stringify(userQueueIds),
	});

	const {
		count: countPending,
		loading: loadingPending,
	} = useTickets({
		status: "pending",
		showAll: "true",
		withUnreadMessages: "false",
		queueIds: JSON.stringify(userQueueIds),
	});

	const {
		count: countClosed,
		loading: loadingClosed,
	} = useTickets({
		status: "closed",
		showAll: "true",
		withUnreadMessages: "false",
		queueIds: JSON.stringify(userQueueIds),
		period,
	});

	useEffect(() => {
		let isMounted = true;

		const loadDashboard = async () => {
			const shouldShowInitialLoader = !dashboardData;

			if (shouldShowInitialLoader) {
				setLoading(true);
			}

			setRefreshing(true);

			try {
				const periodParam = JSON.stringify(period);
				const [dashboardResponse, ratingsResponse] = await Promise.all([
					api.get("/dashboard/", { params: { period: periodParam } }),
					api.get("/rating/", {
						params: {
							filters: JSON.stringify({ period: "4" }),
							pageNumber: 1,
						},
					}),
				]);

				if (!isMounted) return;

				const data = dashboardResponse.data;
				const waitAverage = formatAverageTime(data.averageTimePending);
				const serviceAverage = formatAverageTime(data.averageTimeOpen);
				const avgRatingRow =
					Array.isArray(data.averageRating) && data.averageRating[0]
						? data.averageRating[0]
						: null;
				const averageRating =
					avgRatingRow?.avgRating != null ? Number(avgRatingRow.avgRating) : 0;

				setDashboardData({
					contactsCount: data.contactsCount ?? 0,
					contactsToday: data.contactsToday ?? 0,
					ticketsClosedCount: data.ticketsClosedCount ?? 0,
					messagesSentCount: data.messagesSentCount ?? 0,
					connectionsCount: data.connectionsCount ?? 0,
					messageCount: data.messageCount ?? 0,
					averageTimeTicketPending: waitAverage.formatted,
					averageTimeTicketOpen: serviceAverage.formatted,
					averageTimePendingMinutes: waitAverage.minutes,
					averageTimeOpenMinutes: serviceAverage.minutes,
					averageRating,
					ratingCount: data.ratingCount ?? 0,
					leadsByConnection: Array.isArray(data.leadsByConnection)
						? data.leadsByConnection
						: [],
					dataChartAttendants: Array.isArray(data.dataChartAttendants)
						? data.dataChartAttendants
						: [],
					dataProgressDepartmentBar: Array.isArray(data.dataProgressDepartmentBar)
						? data.dataProgressDepartmentBar
						: [],
					dataProgressServiceBar: Array.isArray(data.dataProgressServiceBar)
						? data.dataProgressServiceBar
						: [],
					dataBarRating: Array.isArray(data.dataBarRating) ? data.dataBarRating : [],
					cloudMessages: {
						messagesFromMe: data.cloudMessages?.messagesFromMe ?? 0,
						messagesFromClient: data.cloudMessages?.messagesFromClient ?? 0,
						tickets: Array.isArray(data.cloudMessages?.tickets)
							? data.cloudMessages.tickets.length
							: data.cloudMessages?.tickets ?? 0,
						sessions: data.cloudMessages?.sessions ?? 0,
					},
				});

				setLastRatings(
					Array.isArray(ratingsResponse.data?.tickets)
						? ratingsResponse.data.tickets.slice(0, 6)
						: []
				);
				setLastUpdatedAt(new Date());
			} catch (err) {
				if (!isMounted) return;

				toastError(err);
				setDashboardData(defaultKpi);
				setLastRatings([]);
			} finally {
				if (!isMounted) return;
				setLoading(false);
				setRefreshing(false);
			}
		};

		loadDashboard();

		return () => {
			isMounted = false;
		};
	}, [period.end, period.start, refreshKey]);

	const metrics = dashboardData || defaultKpi;

	const leadsByConnection = useMemo(
		() =>
			[...(metrics.leadsByConnection || [])]
				.filter((item) => Number(item.leadsCount) > 0)
				.sort((first, second) => Number(second.leadsCount) - Number(first.leadsCount)),
		[metrics.leadsByConnection]
	);

	const openByAttendant = useMemo(
		() =>
			[...(metrics.dataChartAttendants || [])]
				.map((item) => ({
					name: item.name,
					value: Number(item.amount) || 0,
				}))
				.filter((item) => item.value > 0)
				.sort((first, second) => second.value - first.value)
				.slice(0, 6),
		[metrics.dataChartAttendants]
	);

	const closedByQueue = useMemo(
		() =>
			[...(metrics.dataProgressDepartmentBar || [])]
				.map((item) => ({
					name: item.name,
					value: Number(item.value) || 0,
					fill: item.fill,
				}))
				.filter((item) => item.value > 0)
				.sort((first, second) => second.value - first.value)
				.slice(0, 6),
		[metrics.dataProgressDepartmentBar]
	);

	const closedByAttendant = useMemo(
		() =>
			[...(metrics.dataProgressServiceBar || [])]
				.map((item) => ({
					name: item.name,
					value: Number(item.value) || 0,
				}))
				.filter((item) => item.value > 0)
				.sort((first, second) => second.value - first.value)
				.slice(0, 6),
		[metrics.dataProgressServiceBar]
	);

	const ratingByAttendant = useMemo(
		() =>
			[...(metrics.dataBarRating || [])]
				.map((item) => ({
					name: item.name,
					value: Number(item.rating) || 0,
					reviews: Number(item.average) || 0,
				}))
				.filter((item) => item.value > 0 && item.reviews > 0)
				.sort((first, second) => second.value - first.value)
				.slice(0, 6),
		[metrics.dataBarRating]
	);

	const leadsChartData = useMemo(
		() =>
			leadsByConnection.slice(0, 6).map((item) => ({
				name: truncateLabel(item.name, 18),
				fullName: item.name,
				leads: Number(item.leadsCount) || 0,
				channelLabel: formatChannelLabel(item.channel),
				fill: getChannelColor(item.channel),
			})),
		[leadsByConnection]
	);

	const topConnection = leadsByConnection[0];
	const topQueue = closedByQueue[0];
	const topAttendant = closedByAttendant[0];
	const averageRatingLabel = getAverageRatingLabel(metrics.averageRating);
	const totalLeads = leadsByConnection.reduce(
		(accumulator, item) => accumulator + Number(item.leadsCount || 0),
		0
	);
	const pendingVsOpenGap = Number(countPending || 0) - Number(countOpen || 0);

	const heroInsights = useMemo(() => {
		const items = [];

		if (pendingVsOpenGap > 0) {
			items.push({
				title: "Fila de espera acima do volume em atendimento",
				description: `${formatNumber(
					pendingVsOpenGap
				)} ticket(s) a mais aguardando do que em conversa agora.`,
				toneClassName:
					"border-warning/20 bg-warning/10 text-warning",
			});
		}

		if (metrics.averageTimePendingMinutes > metrics.averageTimeOpenMinutes) {
			items.push({
				title: "Espera maior do que o tempo de atendimento",
				description:
					"Vale revisar distribuição de filas e priorização para reduzir o tempo de resposta.",
				toneClassName:
					"border-primary/20 bg-primary/10 text-primary",
			});
		}

		if (metrics.averageRating >= 4.5 && metrics.ratingCount > 0) {
			items.push({
				title: "Satisfação em alta",
				description: `${formatNumber(metrics.ratingCount)} avaliação(ões) com média ${Number(
					metrics.averageRating || 0
				).toFixed(1)} no período recente.`,
				toneClassName:
					"border-success/20 bg-success/10 text-success",
			});
		}

		if (!items.length) {
			items.push({
				title: "Operação estável",
				description:
					"Os principais indicadores estão equilibrados para acompanhamento diário.",
				toneClassName:
					"border-default-200 bg-default-100 text-default-700 dark:border-white/10 dark:bg-white/5 dark:text-default-200",
			});
		}

		return items.slice(0, 3);
	}, [
		metrics.averageRating,
		metrics.averageTimeOpenMinutes,
		metrics.averageTimePendingMinutes,
		metrics.ratingCount,
		pendingVsOpenGap,
	]);

	const isPresetActive = (days) => {
		const preset = getDefaultPeriod(days);
		return preset.start === period.start && preset.end === period.end;
	};

	const metricCards = [
		{
			title: "Em atendimento agora",
			value: loadingOpen ? "..." : formatNumber(countOpen),
			hint: userQueueIds.length
				? "Considera as filas vinculadas ao seu usuário."
				: "Considera toda a operação neste momento.",
			icon: <IconUsers className="size-5" />,
			toneClassName: "bg-primary/10 text-primary",
		},
		{
			title: "Aguardando retorno",
			value: loadingPending ? "..." : formatNumber(countPending),
			hint: "Conversas pendentes que ainda precisam de ação.",
			icon: <IconClock className="size-5" />,
			toneClassName: "bg-warning/10 text-warning",
		},
		{
			title: "Resolvidos no período",
			value: loadingClosed ? "..." : formatNumber(countClosed),
			hint: `Fechamentos entre ${formatDateLabel(period.start)} e ${formatDateLabel(
				period.end
			)}.`,
			icon: <IconCheck className="size-5" />,
			toneClassName: "bg-success/10 text-success",
		},
		{
			title: "Novos contatos hoje",
			value: loading ? "..." : formatNumber(metrics.contactsToday),
			hint: "Mostra o ritmo de entrada de novos contatos no dia.",
			icon: <IconArrowUpRight className="size-5" />,
			toneClassName: "bg-info/10 text-info",
		},
		{
			title: "Mensagens no período",
			value: loading ? "..." : formatNumber(metrics.messageCount),
			hint: loading
				? ""
				: `${formatNumber(metrics.messagesSentCount)} enviadas pela equipe.`,
			icon: <IconMessage className="size-5" />,
			toneClassName: "bg-secondary/10 text-secondary",
		},
		{
			title: "Satisfação média",
			value:
				loading || metrics.ratingCount === 0
					? loading
						? "..."
						: "-"
					: Number(metrics.averageRating || 0).toFixed(1),
			hint: averageRatingLabel,
			icon: <IconStar filled className="size-5" toneClassName="text-warning" />,
			toneClassName: "bg-warning/10 text-warning",
			footer:
				loading || metrics.ratingCount === 0 ? null : (
					<div className="flex items-center gap-2">
						<RatingStars value={metrics.averageRating} className="size-3.5" />
						<span className="text-xs text-default-500 dark:text-default-400">
							{formatNumber(metrics.ratingCount)} avaliação(ões)
						</span>
					</div>
				),
		},
	];

	if (!user) return null;

	return (
		<MainContainer>
			<div className="app-admin-page">
				<div className="app-admin-page__scroll">
					<AppPageTitle title="Dashboard" subtitle="Visão operacional" />

					<div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.75fr)_380px]">
						<div className="card">
							<div className="card-body flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
								<div className="min-w-0 space-y-4">
									<span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
										Painel principal
									</span>
									<div>
										<h2 className="m-0 text-2xl font-semibold text-default-900 dark:text-default-50">
											Operação centralizada em dados relevantes
										</h2>
										<p className="mt-2 mb-0 max-w-2xl text-sm leading-6 text-default-500 dark:text-default-400">
											Acompanhe o que está acontecendo agora, quais canais geram mais
											entrada, onde o time está concentrando esforço e como o cliente
											está percebendo o atendimento.
										</p>
									</div>
									<div className="grid gap-3 md:grid-cols-3">
										<MiniStat
											label="Canal com mais leads"
											value={topConnection?.name || "Sem dados"}
											hint={
												topConnection
													? `${formatNumber(topConnection.leadsCount)} leads no período.`
													: "Conecte canais para acompanhar a origem dos atendimentos."
											}
										/>
										<MiniStat
											label="Setor com mais fechamentos"
											value={topQueue?.name || "Sem dados"}
											hint={
												topQueue
													? `${formatNumber(topQueue.value)} ticket(s) resolvido(s).`
													: "Ainda não houve encerramentos suficientes no período."
											}
										/>
										<MiniStat
											label="Maior produtividade"
											value={topAttendant?.name || "Sem dados"}
											hint={
												topAttendant
													? `${formatNumber(topAttendant.value)} finalização(ões) no período.`
													: "Sem produtividade registrada no intervalo atual."
											}
										/>
									</div>
								</div>

								<div className="grid w-full gap-3 lg:max-w-sm">
									{heroInsights.map((item) => (
										<InsightCard
											key={item.title}
											title={item.title}
											description={item.description}
											toneClassName={item.toneClassName}
										/>
									))}
								</div>
							</div>
						</div>

						<SectionCard
							title="Período analisado"
							subtitle={`Última atualização: ${
								lastUpdatedAt ? formatDateTime(lastUpdatedAt) : "aguardando carga"
							}`}
							action={
								<button
									type="button"
									className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
									onClick={() => setRefreshKey((current) => current + 1)}
									disabled={refreshing}
								>
									<IconRefresh className={`size-4 ${refreshing ? "animate-spin" : ""}`.trim()} />
									{refreshing ? "Atualizando..." : "Atualizar"}
								</button>
							}
						>
							<div className="space-y-4">
								<div className="flex flex-wrap gap-2">
									{PERIOD_PRESETS.map((preset) => (
										<button
											key={preset.id}
											type="button"
											onClick={() => setPeriod(getDefaultPeriod(preset.days))}
											className={`btn btn-sm ${
												isPresetActive(preset.days)
													? "bg-primary text-white"
													: "border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
											}`.trim()}
										>
											{preset.label}
										</button>
									))}
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<label className="flex items-center gap-2 text-sm font-medium text-default-700 dark:text-default-200">
											<IconCalendar className="size-4 text-default-500" />
											Data inicial
										</label>
										<input
											type="date"
											value={period.start}
											onChange={(event) =>
												setPeriod((current) => ({
													...current,
													start: event.target.value,
												}))
											}
											className="form-input form-input-sm w-full"
										/>
									</div>
									<div className="space-y-2">
										<label className="flex items-center gap-2 text-sm font-medium text-default-700 dark:text-default-200">
											<IconCalendar className="size-4 text-default-500" />
											Data final
										</label>
										<input
											type="date"
											value={period.end}
											onChange={(event) =>
												setPeriod((current) => ({
													...current,
													end: event.target.value,
												}))
											}
											className="form-input form-input-sm w-full"
										/>
									</div>
								</div>

								<div className="rounded-2xl border border-default-200 bg-default-50 px-4 py-3 dark:border-white/10 dark:bg-white/5">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Resumo do intervalo
									</p>
									<p className="m-0 text-sm font-medium text-default-700 dark:text-default-200">
										{formatDateLabel(period.start)} até {formatDateLabel(period.end)}
									</p>
								</div>
							</div>
						</SectionCard>
					</div>

					<div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
						{metricCards.map((card) => (
							<MetricCard key={card.title} {...card} />
						))}
					</div>

					<div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
						<SectionCard
							title="Saúde da operação"
							subtitle="Indicadores úteis para acompanhar velocidade, volume e origem da demanda."
						>
							<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
								<MiniStat
									label="Tempo médio de espera"
									value={loading ? "..." : metrics.averageTimeTicketPending}
									hint="Tempo até o ticket sair da fila de espera."
									valueClassName="text-warning"
								/>
								<MiniStat
									label="Tempo médio de atendimento"
									value={loading ? "..." : metrics.averageTimeTicketOpen}
									hint="Tempo médio gasto em tickets já assumidos."
									valueClassName="text-success"
								/>
								<MiniStat
									label="Contatos da base"
									value={loading ? "..." : formatNumber(metrics.contactsCount)}
									hint="Total de contatos disponíveis para relacionamento."
								/>
								<MiniStat
									label="Sessões WABA"
									value={loading ? "..." : formatNumber(metrics.cloudMessages.sessions)}
									hint="Sessões iniciadas em canais oficiais no período."
								/>
							</div>

							<div className="mt-4 grid gap-4 md:grid-cols-3">
								<div className="rounded-2xl border border-default-200/80 bg-default-50/70 p-4 dark:border-white/10 dark:bg-white/5">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Mensagens enviadas
									</p>
									<p className="m-0 text-lg font-semibold text-default-900 dark:text-default-50">
										{loading ? "..." : formatNumber(metrics.messagesSentCount)}
									</p>
									<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
										Volume enviado pela equipe no intervalo selecionado.
									</p>
								</div>
								<div className="rounded-2xl border border-default-200/80 bg-default-50/70 p-4 dark:border-white/10 dark:bg-white/5">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Recebidas via WABA
									</p>
									<p className="m-0 text-lg font-semibold text-default-900 dark:text-default-50">
										{loading ? "..." : formatNumber(metrics.cloudMessages.messagesFromClient)}
									</p>
									<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
										Mensagens recebidas em canais oficiais.
									</p>
								</div>
								<div className="rounded-2xl border border-default-200/80 bg-default-50/70 p-4 dark:border-white/10 dark:bg-white/5">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Tickets WABA
									</p>
									<p className="m-0 text-lg font-semibold text-default-900 dark:text-default-50">
										{loading ? "..." : formatNumber(metrics.cloudMessages.tickets)}
									</p>
									<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
										Quantidade de tickets oficiais abertos no período.
									</p>
								</div>
							</div>
						</SectionCard>

						<SectionCard
							title="Últimas avaliações"
							subtitle="Feedback recente de clientes para leitura rápida."
						>
							<div className="mb-4 rounded-2xl border border-warning/20 bg-warning/10 px-4 py-3">
								<div className="flex items-center justify-between gap-3">
									<div>
										<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-warning">
											Satisfação recente
										</p>
										<p className="m-0 text-2xl font-semibold text-default-900 dark:text-default-50">
											{loading
												? "..."
												: metrics.ratingCount > 0
													? Number(metrics.averageRating || 0).toFixed(1)
													: "-"}
										</p>
									</div>
									<RatingStars value={metrics.averageRating} />
								</div>
								<p className="mt-3 mb-0 text-sm text-default-600 dark:text-default-300">
									{loading
										? "Carregando feedback recente..."
										: `${formatNumber(metrics.ratingCount)} avaliação(ões) com média classificada como "${averageRatingLabel.toLowerCase()}".`}
								</p>
							</div>

							<div className="space-y-3">
								{lastRatings.length === 0 ? (
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										Nenhuma avaliação recente encontrada.
									</p>
								) : (
									lastRatings.map((item) => (
										<div
											key={item.id}
											className="rounded-2xl border border-default-200/80 bg-default-50/70 p-4 dark:border-white/10 dark:bg-white/5"
										>
											<div className="mb-2 flex items-start justify-between gap-3">
												<div className="min-w-0">
													<p className="m-0 truncate text-sm font-semibold text-default-800 dark:text-default-100">
														{item.contact?.name || "Contato sem nome"}
													</p>
													<p className="mt-1 mb-0 text-xs text-default-500 dark:text-default-400">
														{formatDateTime(item.createdAt)}
													</p>
												</div>
												<RatingStars value={item.rating} className="size-3.5" />
											</div>
											<p className="m-0 text-sm leading-6 text-default-600 dark:text-default-300">
												{item.ratingDescription || "Sem comentário adicional."}
											</p>
										</div>
									))
								)}
							</div>
						</SectionCard>
					</div>

					<div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
						<SectionCard
							title="Leads por canal"
							subtitle={`Total de ${formatNumber(totalLeads)} lead(s) distribuídos pelos canais ativos.`}
							bodyClassName="h-full"
						>
							{leadsChartData.length === 0 ? (
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									Nenhum canal com leads no período selecionado.
								</p>
							) : (
								<div className="h-[320px]">
									<ResponsiveContainer width="100%" height="100%">
										<BarChart
											data={leadsChartData}
											layout="vertical"
											margin={{ top: 0, right: 12, left: 8, bottom: 0 }}
										>
											<CartesianGrid
												strokeDasharray="3 3"
												stroke={darkMode ? "#3f3f46" : "#e5e7eb"}
											/>
											<XAxis
												type="number"
												stroke={darkMode ? "#a1a1aa" : "#71717a"}
												tick={{ fontSize: 11 }}
											/>
											<YAxis
												type="category"
												dataKey="name"
												width={110}
												stroke={darkMode ? "#a1a1aa" : "#71717a"}
												tick={{ fontSize: 12 }}
											/>
											<Tooltip content={<DashboardChartTooltip darkMode={darkMode} />} />
											<Bar dataKey="leads" radius={[0, 6, 6, 0]} barSize={18}>
												{leadsChartData.map((entry) => (
													<Cell key={entry.fullName} fill={entry.fill} />
												))}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</div>
							)}
						</SectionCard>

						<SectionCard
							title="Conversas abertas por atendente"
							subtitle="Mostra quem está com maior carga ativa agora."
						>
							<RankingList
								items={openByAttendant}
								emptyText="Nenhum atendente com conversas abertas neste momento."
								valueFormatter={(item) =>
									`${formatNumber(item.value)} em aberto`
								}
								getBarClassName={() => "bg-primary"}
							/>
						</SectionCard>

						<SectionCard
							title="Encerramentos por setor"
							subtitle="Ajuda a identificar onde o fluxo está concluindo mais tickets."
						>
							<RankingList
								items={closedByQueue}
								emptyText="Nenhum setor com tickets encerrados no período."
								valueFormatter={(item) => `${formatNumber(item.value)} fechados`}
								getBarClassName={() => ""}
								getBarStyle={(item, width) => ({
									width: `${width}%`,
									backgroundColor: item.fill || "#6366f1",
								})}
							/>
						</SectionCard>
					</div>

					<div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
						<SectionCard
							title="Produtividade por atendente"
							subtitle="Ranking de finalizações para acompanhar o volume entregue."
						>
							<RankingList
								items={closedByAttendant}
								emptyText="Nenhum atendente com finalizações no período."
								valueFormatter={(item) => `${formatNumber(item.value)} finalizados`}
								getBarClassName={() => "bg-success"}
							/>
						</SectionCard>

						<SectionCard
							title="Qualidade por atendente"
							subtitle="Média das avaliações recebidas por quem teve feedback no período."
						>
							{ratingByAttendant.length === 0 ? (
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									Ainda não há avaliações suficientes para montar o ranking.
								</p>
							) : (
								<div className="space-y-4">
									{ratingByAttendant.map((item) => (
										<div
											key={item.name}
											className="rounded-2xl border border-default-200/80 bg-default-50/70 p-4 dark:border-white/10 dark:bg-white/5"
										>
											<div className="flex items-start justify-between gap-3">
												<div className="min-w-0">
													<p className="m-0 truncate text-sm font-semibold text-default-800 dark:text-default-100">
														{item.name}
													</p>
													<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
														{formatNumber(item.reviews)} avaliação(ões) recebida(s)
													</p>
												</div>
												<div className="text-right">
													<p className="m-0 text-lg font-semibold text-default-900 dark:text-default-50">
														{Number(item.value).toFixed(1)}
													</p>
													<RatingStars value={item.value} className="size-3.5" />
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</SectionCard>
					</div>
				</div>
			</div>
		</MainContainer>
	);
}
